import { ApplicationConfig, importProvidersFrom, isDevMode, provideZonelessChangeDetection } from '@angular/core'
import { provideClientHydration, withEventReplay } from '@angular/platform-browser'
import { provideRouter, withComponentInputBinding, withPreloading, withViewTransitions } from '@angular/router'
import { routes } from './app.routes'
import { RolesBasedPreloadingStrategy } from './core/preloading/roles-based-preloading.strategy'
import {
	AutoRefreshTokenService,
	createInterceptorCondition,
	INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
	IncludeBearerTokenCondition,
	includeBearerTokenInterceptor,
	provideKeycloak,
	UserActivityService,
} from 'keycloak-angular'
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http'
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental'
import { headerInterceptor } from './core/http/interceptors/header.interceptor'
import { BazargahApiModule, Configuration, ConfigurationParameters } from 'api'
import { environment } from '../environments/environment'
import { provideOptimus } from '@openng/optimus-ui/config'
import { errorInterceptor } from './core/http/interceptors/error.interceptor'
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api'
import { provideTranslateService } from '@ngx-translate/core'
import { requestBodyInterceptor } from './core/http/interceptors/request-body.interceptor'
import { initializeNavigationService } from './core/navigation.service'
import { PersianDateTimePickerService } from 'persian-date-time-picker-signal'
import MyPreset from '../../public/assets/preset'
import { provideServiceWorker } from '@angular/service-worker'
import { NumberFormatterPipe } from './shared/pipes/number-formatter.pipe'
import { withDevtools } from '@tanstack/angular-query-experimental/devtools'
import { provideAnimations } from '@angular/platform-browser/animations'

// Authentication Configuration
const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
	urlPattern: /^((https|http):\/+\/.*)?$/i,
	bearerPrefix: 'Bearer',
})

export function apiConfigFactory(): Configuration {
	const params: ConfigurationParameters = {
		basePath: environment.apiUrl,
	}
	return new Configuration(params)
}

// TanStack Query Configuration
function createQueryClient(): QueryClient {
	return new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: 1000 * 30, // Increased to 30 seconds for better performance
				refetchOnWindowFocus: false, // Reduced unnecessary refetches
				refetchOnReconnect: true,
				retry: 2, // Increased retry attempts
				experimental_prefetchInRender: true,
			},
			mutations: {
				retry: 0,
			},
		},
	})
}

// Core Application Providers
const coreProviders = [
	provideZonelessChangeDetection(),
	// هیدریشن باید در کانفیگ مشترک (سرور + کلاینت) باشد؛ اگر فقط در main.ts (کلاینت)
	// ثبت شود، سرور اطلاعات hydration را serialize نمی‌کند و مرورگر کل اپ را از نو
	// رندر می‌کند (NG0505) — دقیقاً همان پنجره‌ای که کلیک‌ها (مثل دکمه «ورود» هدر)
	// از بین می‌رفتند. withEventReplay همین کلیک‌های پیش از hydration را پخش می‌کند.
	provideClientHydration(withEventReplay()),
	provideRouter(
		routes,
		withComponentInputBinding(),
		withViewTransitions(),
		withPreloading(RolesBasedPreloadingStrategy)
	),
	provideAnimations(),
]

// Authentication Providers
//
// SSR: `keycloak-js` references `window` during `init()` (a `'Cordova' in window`
// check), and `keycloak-angular` always registers an app-initializer that calls
// `init()` whenever `initOptions` is provided. On the server that throws
// `ReferenceError: window is not defined`. The library documents that omitting
// `initOptions` skips the initializer (the `Keycloak` constructor itself is
// server-safe), so we only pass `initOptions` on the browser. The
// `includeBearerTokenInterceptor` stays registered everywhere — it is SSR-safe
// and simply passes requests through when Keycloak is unauthenticated.
const isBrowser = typeof window !== 'undefined'
const silentCheckSsoRedirectUri = isBrowser ? window.location.origin + '/silent-check-sso.html' : ''

const authProviders = [
	provideKeycloak({
		config: {
			url: environment.authUrl,
			realm: 'beauty-boss',
			clientId: 'web-app-client',
		},
		// Only init on the browser; on the server this is `undefined`, which
		// makes keycloak-angular skip its init app-initializer entirely.
		initOptions:
			isBrowser ?
				{
					onLoad: 'check-sso',
					silentCheckSsoRedirectUri,
					pkceMethod: 'S256',
				}
			:	undefined,
		providers: [AutoRefreshTokenService, UserActivityService],
	}),
	{
		provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
		useValue: [urlCondition],
	},
]

// HTTP & API Providers
const httpProviders = [
	provideHttpClient(
		withFetch(),
		withInterceptors([headerInterceptor, includeBearerTokenInterceptor, errorInterceptor, requestBodyInterceptor])
	),
	provideTanStackQuery(
		createQueryClient(),
		withDevtools(() => ({ loadDevtools: !isDevMode() })) // Enable devtools only in development
	),
]

// UI & Component Providers
const uiProviders = [
	provideOptimus({
		theme: {
			preset: MyPreset,
			options: {
				darkModeSelector: '.my-app-dark',
				cssLayer: {
					name: 'optimus',
					order: 'theme, base, utilities, optimus',
				},
			},
		},
	}),
	MessageService,
	ConfirmationService,
	NumberFormatterPipe,
]

// Service & Utility Providers
const serviceProviders = [
	initializeNavigationService,
	PersianDateTimePickerService,
	provideTranslateService({
		fallbackLang: 'fa',
	}),
]

// External Module Providers
const externalModuleProviders = [importProvidersFrom(BazargahApiModule.forRoot(apiConfigFactory))]

// PWA & Production Providers
const pwaProviders = [
	provideServiceWorker('ngsw-worker.js', {
		enabled: !isDevMode(),
		registrationStrategy: 'registerWhenStable:30000',
	}),
]

export const appConfig: ApplicationConfig = {
	providers: [
		...coreProviders,

		...authProviders,

		...httpProviders,

		...uiProviders,

		...serviceProviders,

		...externalModuleProviders,

		...pwaProviders,
	],
}
